<template>
    <div class="ob1-price" :class="classObject">
        <p class="visually-hidden">
            {{ priceAccessibility }}
        </p>
        <p aria-hidden="true">
            <template v-if="priceLabel">
                <span class="ob1-price-amount-label ">{{ priceLabel }}</span>
            </template>
            <template v-else>
                <span v-if="showPiggyBank" class="icon icon-phone-fund-euro font-size-24 text-dark" />
                <span :class="amountClassObject">{{ formattedPrice | replacePointByComma | centsHandler }}&#8239;€</span>
                <span v-if="showByMonth" class="ob1-price-by-month">/mois</span>
            </template>
            <span v-if="showPromoDuration" :class="promoDurationClassObject">
                <span>pendant {{ duration }} mois puis</span>
                <span class="ob1-price-promo-duration-by-month">{{ formattedInitialPrice | replacePointByComma | centsHandler }}&#8239;€/mois</span>
            </span>
            <span v-if="priceDetails && priceDetails.length > 0" :class="priceDetailsClassObject" v-html="priceDetails" />
        </p>
    </div>
</template>

<script>
export default {
    name:"OfferPrice",
    filters: {
        replacePointByComma(price) {
            if (price !== undefined) {
                return price.toString().replace('.', ',')
            }
        },
        centsHandler(price) {
            if (price !== undefined) {
                const wholePart = price.split(',')[0];
                const cents = price.split(',')[1];
                if (typeof cents !== 'undefined') {
                    // Si les centimes sont à '0' ou '00', on ne les affiche pas
                    if (cents === '0' || cents === '00') {
                        return wholePart;
                        // Si seulement les dixième sont présents sans des centièmes, on ajoute un 0 (cas d'un float renvoyé par le back)
                    } else if (cents.length === 1) {
                        return price.concat('0');
                    } else {
                        return price;
                    }
                } else {
                    return price;
                }
            }
        }
    },
    props: {
        // prix
        price: {
            type: Number,
            required: false,
            default: 0
        },
        // affiche icone cochon tirelire pour les offre fmr
        showPiggyBank: {
            type: Boolean,
            default: false
        },
        // affiche le bloc '/mois'
        showByMonth: {
            type: Boolean,
            default: false
        },
        // affiche le bloc 'pendant n mois...'
        showPromoDuration: {
            type: Boolean,
            default: false
        },
        // affiche le bloc 'jusqu’à la fin de vos promotions en cours'
        showUntilEndOfPromo: {
            type: Boolean,
            default: false
        },
        // affiche le bloc 'puis n€/mois à la fin de vos promos'
        showEndOfPromotionPrice: {
            type: Boolean,
            default: false
        },
        // durée de la promo
        duration: {
            type: Number,
            required: false,
            default: 0
        },
        // prix hors promo
        initialPrice: {
            type: Number,
            required: false,
            default: 0
        },
        // affiche le prix au format mini
        isMini: {
            type: Boolean,
            default: false,
        },
        // affiche le prix au format inverse
        isInverse: {
            type: Boolean,
            default: false
        },
        // affiche le prix au format compact
        isCompact: {
            type: Boolean,
            default: false
        },
        // label textuel optionnel qui vient surcharger l'affichage du prix
        priceLabel: {
            type: String,
            default: null
        },
        // label textuel optionnel pour afficher la durée d'engagement
        priceDetails : {
            type: String,
            default: null
        },
        // précise si le prix en entrée du composant est exprimé en centimes (XXXX) ou déjà au format float (XX.XX)
        isCentsFormat : {
            type: Boolean,
            default: false
        },
        // précise la classe css à utiliser pour le prix custom
        custom : {
            type: String,
            default: null
        },
        // précise la classe css à utiliser pour les lignes de mentions custom
        customPromoDuration : {
            type: String,
            default: null
        },
        customPriceDetails : {
            type: String,
            default: null
        }

    },
    computed: {
        // prix formaté en fonction de la prop "isCentsFormat"
        // - si false, pas de transformation, on a déjà un prix au format "XX,XX €"
        // - si true, la valeur passée à la prop "price" est en centimes, on divise donc par 100 pour avoir la valeur au format "XX,XX €"
        formattedPrice: function () {
            if (this.isCentsFormat) {
                return (this.price / 100).toString()
            } else {
                return this.price
            }
        },
        formattedInitialPrice: function () {
            if (this.isCentsFormat) {
                return (this.initialPrice / 100).toString()
            } else {
                return this.initialPrice
            }
        },


        // prix vocalisé lorsqu'on combine le prix avec d'autres informations
        priceAccessibility: function () {
            let pricePrefix;
            if (this.priceLabel) {
                pricePrefix = `${this.priceLabel} `;
            } else {
                pricePrefix = ' ' + this.formattedPrice + ' euros' + (this.showByMonth ? ' par mois ' : ' ');
            }
            if (this.showPromoDuration) {
                pricePrefix = pricePrefix + 'pendant ' + this.duration + ' mois puis ' + this.formattedInitialPrice + ' euros par mois';
            } else if (this.showUntilEndOfPromo) {
                pricePrefix = pricePrefix + 'jusqu’à la fin de vos promotions en cours puis ' + this.formattedInitialPrice + ' euros par mois';
            } else if (this.showEndOfPromotionPrice) {
                pricePrefix = pricePrefix + 'puis ' + this.formattedInitialPrice + ' euros par mois à la fin de vos promotions';
            }
            return pricePrefix;
        },
        // recombinaison des classes nécessaires à l'affichage du composant
        classObject: function () {
            return {
                'ob1-price-inverse': this.isInverse,
                'ob1-price-mini': this.isMini,
                'ob1-price-compact': this.isCompact
            }
        },
        // recombinaison des classes nécessaires à l'affichage du prix
        amountClassObject: function () {
            if (this.custom != null) {
                return 'ob1-price-amount-custom ' + this.custom;
            } else {
                return 'ob1-price-amount';
            }
        },
        // recombinaison des classes nécessaires à l'affichage des lignes de mentions
        promoDurationClassObject: function () {
            if (this.customPromoDuration != null) {
                return 'ob1-price-promo-duration-custom ' + this.customPromoDuration;
            } else {
                return 'ob1-price-promo-duration';
            }
        },
        priceDetailsClassObject: function () {
            if (this.customPriceDetails != null) {
                return 'ob1-price-promo-duration-custom ' + this.customPriceDetails;
            } else {
                return 'ob1-price-promo-duration';
            }
        }
    }
}
</script>
